<section class="history-panel">
  <header class="history-panel__header">
    <div class="history-panel__heading">
      <h2 class="history-panel__title">Payment history</h2>
      <p class="history-panel__subtitle">
        Every payment attempt across all organizations, with the invoice that
        was generated for it.
      </p>
      <div class="history-panel__stats" *ngIf="!isLoading && transactions.length">
        <span class="history-stat">
          <strong>{{ totalCount }}</strong>
          {{ totalCount === 1 ? "transaction" : "transactions" }}
        </span>
        <span class="history-stat" *ngIf="totalPages > 1">
          Page <strong>{{ currentPage + 1 }}</strong> of
          <strong>{{ totalPages }}</strong>
        </span>
      </div>
    </div>
  </header>

  <form
    [formGroup]="paymentHistoryForm"
    (ngSubmit)="handlePaymentHistoryFilters()"
    class="history-filters"
  >
    <div class="history-filters__head">
      <span class="history-filters__title">
        <i class="material-icons">filter_list</i>
        Filters
      </span>
      <button
        type="button"
        class="history-filters__reset"
        *ngIf="hasActiveFilters"
        (click)="handlePaymentHistoryResetForm()"
      >
        Reset all
      </button>
    </div>

    <div class="history-filters__grid">
      <mat-form-field appearance="outline" class="history-filters__search">
        <mat-label>Search</mat-label>
        <i class="material-icons field-icon" matPrefix>search</i>
        <input
          matInput
          placeholder="Username or organization"
          formControlName="searchBy"
        />
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Payment status</mat-label>
        <mat-select placeholder="Any" formControlName="isSuccess">
          <mat-option [value]="null">Any</mat-option>
          <mat-option [value]="true">Successful</mat-option>
          <mat-option [value]="false">Failed</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Transaction type</mat-label>
        <mat-select placeholder="Any" formControlName="transactionType">
          <mat-option [value]="null">Any</mat-option>
          <mat-option [value]="transactionType.FullPurchase">
            Full purchase
          </mat-option>
          <mat-option [value]="transactionType.Subscription">
            Subscription
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Invoice status</mat-label>
        <mat-select placeholder="Any" formControlName="invoiceGenerated">
          <mat-option [value]="null">Any</mat-option>
          <mat-option [value]="true">Generated</mat-option>
          <mat-option [value]="false">Missing</mat-option>
        </mat-select>
      </mat-form-field>

      <ng-container formGroupName="dateTimeInterval">
        <mat-form-field appearance="outline">
          <mat-label>From</mat-label>
          <input
            matInput
            [max]="paymentHistoryForm?.value?.dateTimeInterval?.endDate"
            [matDatepicker]="pickerStartDate"
            formControlName="startDate"
          />
          <mat-datepicker-toggle
            matSuffix
            [for]="pickerStartDate"
          ></mat-datepicker-toggle>
          <mat-datepicker #pickerStartDate></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>To</mat-label>
          <input
            matInput
            [min]="paymentHistoryForm?.value?.dateTimeInterval?.startDate"
            [matDatepicker]="pickerEndDate"
            formControlName="endDate"
          />
          <mat-datepicker-toggle
            matSuffix
            [for]="pickerEndDate"
          ></mat-datepicker-toggle>
          <mat-datepicker #pickerEndDate></mat-datepicker>
        </mat-form-field>
      </ng-container>
    </div>

    <div class="history-filters__actions">
      <button
        mat-stroked-button
        type="button"
        class="history-filters__clear"
        (click)="handlePaymentHistoryResetForm()"
      >
        Clear
      </button>
      <button mat-flat-button color="primary" type="submit" class="history-filters__apply">
        Apply filters
      </button>
    </div>
  </form>

  <ng-container *ngIf="!isLoading">
    <div class="history-table-wrap" *ngIf="transactions.length">
      <table class="history-table">
        <thead>
          <tr>
            <th scope="col">Account</th>
            <th scope="col">Organization</th>
            <th scope="col">Attempted</th>
            <th scope="col" class="is-numeric">Amount</th>
            <th scope="col">Type</th>
            <th scope="col">Payment</th>
            <th scope="col">Invoice</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let transaction of transactions; trackBy: trackByTransaction"
          >
            <td class="is-primary">{{ transaction.ownerUsername || "—" }}</td>
            <td>{{ transaction.organizationName || "—" }}</td>
            <td>
              <span class="cell-date">
                {{ (transaction.paymentAttemptDate | date: "d MMM y") || "—" }}
              </span>
              <span class="cell-time" *ngIf="transaction.paymentAttemptDate">
                {{ transaction.paymentAttemptDate | date: "HH:mm" }}
              </span>
            </td>
            <td class="is-numeric is-primary">
              {{
                transaction.amountPaid !== null &&
                transaction.amountPaid !== undefined
                  ? (transaction.amountPaid | currency: "USD":"symbol":"1.2-2")
                  : "—"
              }}
            </td>
            <td>
              <span class="type-chip">
                {{ transactionTypeValues[transaction.transactionType] }}
              </span>
            </td>
            <td>
              <span
                class="status-pill"
                [class.status-pill--active]="transaction.isSuccess"
              >
                <span class="status-pill__dot"></span>
                {{ transaction.isSuccess ? "Paid" : "Failed" }}
              </span>
              <span
                class="cell-failure"
                *ngIf="!transaction.isSuccess && transaction.failureReason"
                [title]="transaction.failureReason"
              >
                {{ transaction.failureReason }}
              </span>
            </td>
            <td>
              <span
                class="status-pill"
                [class.status-pill--active]="transaction.invoiceGenerated"
              >
                <span class="status-pill__dot"></span>
                {{ transaction.invoiceGenerated ? "Generated" : "Missing" }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="history-empty" *ngIf="!transactions.length">
      <i class="material-icons history-empty__icon">receipt_long</i>
      <p class="history-empty__title">No transactions found</p>
      <p class="history-empty__text">
        {{
          hasActiveFilters
            ? "No payment attempt matches the current filters."
            : "Payment attempts will appear here as they are processed."
        }}
      </p>
      <button
        type="button"
        class="history-empty__link"
        *ngIf="hasActiveFilters"
        (click)="handlePaymentHistoryResetForm()"
      >
        Reset filters
      </button>
    </div>

    <footer class="history-pager" *ngIf="totalPages > 1">
      <span class="history-pager__info">
        Page {{ currentPage + 1 }} of {{ totalPages }}
      </span>

      <div class="history-pager__controls">
        <button
          type="button"
          mat-icon-button
          class="history-pager__step"
          [disabled]="currentPage === 0"
          (click)="handlePaymentHistoryPageChange(currentPage - 1)"
          aria-label="Previous page"
        >
          <i class="material-icons">chevron_left</i>
        </button>

        <select
          class="history-pager__select"
          aria-label="Select page"
          [(ngModel)]="currentPage"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="handlePaymentHistoryPageSelect($event)"
        >
          <option
            *ngFor="let _ of [].constructor(totalPages); let i = index"
            [ngValue]="i"
          >
            {{ i + 1 }}
          </option>
        </select>

        <button
          type="button"
          mat-icon-button
          class="history-pager__step"
          [disabled]="currentPage === totalPages - 1"
          (click)="handlePaymentHistoryPageChange(currentPage + 1)"
          aria-label="Next page"
        >
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </footer>
  </ng-container>

  <div class="history-table-wrap" *ngIf="isLoading" aria-busy="true">
    <div class="history-skeleton" *ngFor="let placeholder of skeletonRows">
      <div class="skeleton skeleton--line skeleton--line-lg"></div>
      <div class="skeleton skeleton--line"></div>
      <div class="skeleton skeleton--line skeleton--line-sm"></div>
      <div class="skeleton skeleton--pill"></div>
      <div class="skeleton skeleton--pill"></div>
    </div>
  </div>
</section>
